/**
 * diag — @yjsgames/diag-log 앱 어댑터 (도메인별 런타임 DIAG 로그 제어).
 *
 * DIAG 포인트를 코드에 상시 심어두고 레벨로 켜고 끈다 — 재진단 시 재탑재 비용 0.
 * 도메인 키 = boot 설치자 key 컨벤션 (economy/dungeon/settings/...).
 *
 * 런타임 제어 3경로:
 *  ① AutoPlay: `SET_LOG_LEVEL <domain|*> <off|diag|verbose>` (ModuleBrickGlobalExecutor)
 *  ② 브라우저 콘솔(dev/치트 빌드): `window.__diag.set("economy","diag")` / `.domains()` / `.levels()`
 *  ③ localStorage 영속 — 새로고침 후에도 레벨 유지 (QA 세션 연속성)
 */
import { DiagLogRegistry, isDiagLevel, DIAG_LEVELS, type DiagLevel, type DiagLogger } from "@yjsgames/diag-log";
import { CHEAT_ENABLED } from "../env/appFlavor.js";

const STORAGE_KEY = "brick.diagLevels";

function localStoragePersist() {
  if (typeof localStorage === "undefined") return undefined;
  return {
    load(): Record<string, DiagLevel> | null {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Record<string, DiagLevel>) : null;
      } catch {
        return null;
      }
    },
    save(levels: Record<string, DiagLevel>): void {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(levels));
      } catch {
        /* 저장 실패 — 휘발로 동작 */
      }
    },
  };
}

/**
 * 앱 전역 레지스트리 — 모듈/boot이 diag(domain)으로 로거를 얻는다.
 * sink=console.warn: AutoPlay 콘솔 캡처(installConsoleCapture)가 error/warn만 sessions/<id>/console.log로
 * 수집하므로, 레벨을 켠 DIAG 라인이 파일 캡처에 잡히려면 warn 채널이어야 한다 (기본 off라 평시 노이즈 0).
 */
export const diagRegistry = new DiagLogRegistry({
  persist: localStoragePersist(),
  sink: (line) => console.warn(line),
});

/** 도메인 로거 — boot 파일 상단에서 `const log = diag("economy")`. */
export function diag(domain: string): DiagLogger {
  return diagRegistry.logger(domain);
}

/** AutoPlay/콘솔 공용 레벨 변경 — 인자 검증 포함. 성공 시 true. */
export function setDiagLevel(domain: string, level: string): boolean {
  if (!domain || !isDiagLevel(level)) return false;
  diagRegistry.setLevel(domain, level);
  return true;
}

/** dev/치트 빌드 — 브라우저 콘솔 런타임 제어 전역 설치 (라이브 웹은 CHEAT_ENABLED 게이트로 미설치). */
export function installDiagGlobal(): void {
  if (!CHEAT_ENABLED || typeof window === "undefined") return;
  (window as unknown as Record<string, unknown>)["__diag"] = {
    set: (domain: string, level: string) =>
      setDiagLevel(domain, level)
        ? diagRegistry.snapshot()
        : `사용법: __diag.set(<domain|*>, <${DIAG_LEVELS.join("|")}>)`,
    levels: () => diagRegistry.snapshot(),
    domains: () => diagRegistry.domains(),
  };
}
